import React, { useRef, useEffect, useState } from 'react';
import '../../styles/App.css';
import HeroSection from '../sections/HeroSection';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import { motion, useInView, animate } from 'framer-motion';

const stats = [
  { value: '500+', label: 'Students Taught' },
  { value: '2', label: 'Locations' },
  { value: '15+', label: 'Years Experience' },
  { value: 'All Ages', label: 'Welcome' },
];

const whyCards = [
  {
    icon: <SchoolIcon sx={{ fontSize: 52 }} />,
    title: 'Technique Before Trophies',
    desc: "We don't measure progress by how quickly a swimmer levels up. Strong foundations first - technique that actually sticks.",
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 52 }} />,
    title: 'Small Enough to Know Your Name',
    desc: 'A few of the kids we first taught have grown up and come back to coach. We try to be a school where swimmers feel genuinely known.',
  },
  {
    icon: <HealthAndSafetyIcon sx={{ fontSize: 52 }} />,
    title: 'Every Stage of the Journey',
    desc: 'From nervous four-year-olds to competitive junior squads. Over 15 years and 500+ students across Knox and Pymble.',
  },
];


const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const StatCounter = ({ value, label, index }) => {
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  const numeric = parseInt(value, 10);
  const isNumeric = !isNaN(numeric);
  const suffix = isNumeric ? value.slice(String(numeric).length) : '';

  const [displayValue, setDisplayValue] = useState(isNumeric ? `0${suffix}` : value);

  useEffect(() => {
    if (!isInView || !isNumeric) return;
    if (prefersReducedMotion) {
      setDisplayValue(`${numeric}${suffix}`);
      return;
    }
    const controls = animate(0, numeric, {
      duration: 1.6,
      ease: 'easeOut',
      onUpdate: (v) => setDisplayValue(`${Math.round(v)}${suffix}`),
    });
    return () => controls.stop();
  }, [isInView, isNumeric, numeric, suffix]);

  return (
    <Box
      ref={ref}
      component={motion.div}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      sx={{
        textAlign: 'center',
        px: { xs: 2, sm: 5 },
        py: { xs: 1, sm: 0 },
        borderRight: {
          xs: index % 2 === 0 ? `1px solid ${theme.palette.divider}` : 'none',
          sm: index < stats.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${theme.palette.divider}` : 'none',
          sm: 'none',
        },
      }}
    >
      <Typography
        variant="h3"
        sx={{ color: theme.palette.primary.main, fontWeight: 700, lineHeight: 1, mb: 0.5 }}
      >
        {displayValue}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Box>
  );
};

function Home() {
  const theme = useTheme();

  return (
    <div className="home-container">
      <HeroSection />

      {/* Stats Strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          py: { xs: 4, sm: 5 },
          px: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {stats.map((stat, index) => (
          <StatCounter key={index} value={stat.value} label={stat.label} index={index} />
        ))}
      </Box>

      {/* Why Choose SJ Swimming */}
      <Box sx={{ py: { xs: 7, sm: 9 }, px: 2, backgroundColor: theme.palette.background.default }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55 }}
        >
          <Typography
            variant="h3"
            sx={{ textAlign: 'center', mb: 1.5, fontWeight: 700, color: theme.palette.text.primary }}
          >
            Why Choose SJ Swimming
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: { xs: 5, sm: 7 },
              color: theme.palette.text.secondary,
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            More than just swim lessons - we build confidence, skills, and a genuine love for the water.
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            justifyContent: 'center',
            flexWrap: 'wrap',
            maxWidth: 1050,
            mx: 'auto',
          }}
        >
          {whyCards.map((card, i) => (
            <Box
              key={i}
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.13 }}
              sx={{
                textAlign: 'center',
                p: { xs: 4, sm: 5 },
                flex: '1 1 260px',
                maxWidth: 320,
                backgroundColor: theme.palette.background.paper,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Box sx={{ mb: 2.5, color: theme.palette.primary.main }}>
                {card.icon}
              </Box>
              <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 700, color: theme.palette.text.primary }}>
                {card.title}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                {card.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* CTA Banner */}
      <Box
        sx={{
          py: { xs: 8, sm: 11 },
          px: 2,
          textAlign: 'center',
          backgroundColor: theme.palette.primary.main,
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            sx={{ color: '#ffffff', mb: 2, fontWeight: 700 }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              mb: 4,
              maxWidth: 500,
              mx: 'auto',
              fontSize: '1.1rem',
              lineHeight: 1.7,
            }}
          >
            Not sure which program is right for your child? Get in touch and we'll help you find the right fit.
          </Typography>
          <Button
            component={Link}
            to="/contact-us"
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#ffffff',
              color: theme.palette.primary.main,
              fontWeight: 700,
              px: 5,
              py: 1.5,
              fontSize: '1rem',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.92)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Get in Touch
          </Button>
        </Box>
      </Box>
    </div>
  );
}

export default Home;
